const VERSION = 1;
const QUEUE_PREFIX = 'kaizen.smartpdv.checkoutQueue';
const HELD_PREFIX = 'kaizen.smartpdv.heldCarts';

function safeParse(value, fallback) {
  try {
    const parsed = JSON.parse(value);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function key(prefix, organizationId) {
  return `${prefix}.${organizationId}`;
}

export function listQueuedCheckouts(organizationId, storage = globalThis.localStorage) {
  if (!organizationId || !storage) return [];
  return safeParse(storage.getItem(key(QUEUE_PREFIX, organizationId)), []).filter(
    (entry) => entry?.version === VERSION && entry?.payload?.idempotency_key,
  );
}

export function enqueueCheckout(organizationId, entry, storage = globalThis.localStorage) {
  const current = listQueuedCheckouts(organizationId, storage);
  const idempotencyKey = entry?.payload?.idempotency_key;
  if (!idempotencyKey) throw new Error('idempotency_key_required');
  if (current.some((item) => item.payload.idempotency_key === idempotencyKey)) return current;
  const next = [
    ...current,
    {
      version: VERSION,
      id: idempotencyKey,
      queued_at: new Date().toISOString(),
      attempts: 0,
      last_error: null,
      ...entry,
    },
  ];
  storage.setItem(key(QUEUE_PREFIX, organizationId), JSON.stringify(next));
  return next;
}

export function replaceQueuedCheckouts(
  organizationId,
  entries,
  storage = globalThis.localStorage,
) {
  storage?.setItem(key(QUEUE_PREFIX, organizationId), JSON.stringify(entries));
  return entries;
}

export async function flushQueuedCheckouts(
  organizationId,
  sender,
  storage = globalThis.localStorage,
) {
  const queue = listQueuedCheckouts(organizationId, storage);
  const remaining = [];
  const delivered = [];
  for (const entry of queue) {
    try {
      const result = await sender(entry.payload);
      delivered.push({ entry, result });
    } catch (error) {
      remaining.push({
        ...entry,
        attempts: Number(entry.attempts || 0) + 1,
        last_error: String(error?.message || error),
        last_attempt_at: new Date().toISOString(),
      });
      break;
    }
  }
  const untouched = queue.slice(delivered.length + remaining.length);
  replaceQueuedCheckouts(organizationId, [...remaining, ...untouched], storage);
  return { delivered, remaining: [...remaining, ...untouched] };
}

export function listHeldCarts(organizationId, storage = globalThis.localStorage) {
  if (!organizationId || !storage) return [];
  return safeParse(storage.getItem(key(HELD_PREFIX, organizationId)), []);
}

export function holdCart(organizationId, cart, metadata = {}, storage = globalThis.localStorage) {
  if (!cart?.length) return listHeldCarts(organizationId, storage);
  const current = listHeldCarts(organizationId, storage);
  const entry = {
    id: globalThis.crypto?.randomUUID?.() || String(Date.now()),
    label: metadata.label || `Venda ${current.length + 1}`,
    created_at: new Date().toISOString(),
    cart,
    customer_id: metadata.customer_id || '',
    discount_cents: Number(metadata.discount_cents || 0),
  };
  const next = [entry, ...current].slice(0, 20);
  storage.setItem(key(HELD_PREFIX, organizationId), JSON.stringify(next));
  return next;
}

export function removeHeldCart(organizationId, heldId, storage = globalThis.localStorage) {
  const next = listHeldCarts(organizationId, storage).filter((entry) => entry.id !== heldId);
  storage?.setItem(key(HELD_PREFIX, organizationId), JSON.stringify(next));
  return next;
}

